import React, { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { useAppSelector } from 'store/hooks/useAppSelector';
import { selectAppSettingsData } from 'store/selectors/appSelectors';
import { useOrderStatusText } from 'hooks/order';
import { OrderItem } from 'components/account/OrderInfro/OrderList';
import { usePrice } from 'hooks';
import { useTranslation } from 'react-i18next';
import { OrderItemOption } from 'types/Order';

interface OrderListProps {
  orderId: number;
  deliveryPriceKop: number;
  benefitKop: number;
  resultTotalPriceKop: number;
  items: OrderItemOption[];
  status?: number;
}

const OrderList: FC<OrderListProps> = ({
  orderId,
  deliveryPriceKop,
  benefitKop,
  resultTotalPriceKop,
  items,
  status,
}) => {
  const { LOGO_LIGHT } = useAppSelector(selectAppSettingsData);
  const statusText = useOrderStatusText(status);
  const benefitСonvertedPrice = usePrice(benefitKop);
  const deliveryConvertedPrice = usePrice(deliveryPriceKop);
  const resultTotalConvertedPrice = usePrice(resultTotalPriceKop);
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        padding: { xs: '18px 14px', sm: '30px' },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        borderRadius: '20px',
        position: 'relative',
        overflow: 'hidden',
        alignSelf: { xs: 'auto', md: 'flex-start' },
        maxWidth: { xs: '100%', md: '510px' },
        width: '100%',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '0px',
          left: '0px',
          width: '100%',
          height: '100%',
          backgroundColor: 'primary.main',
          zIndex: '-1',
          opacity: '0.2',
        }}
      />
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
          flexGrow: '1',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: { xs: '22px', sm: '32px' },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '20px', sm: '30px' },
              lineHeight: { xs: '22px', sm: '33px' },
              fontWeight: '400',
              color: 'text.primary',
            }}
          >
            {t('order')} {orderId}
          </Typography>
          {statusText ? (
            <Typography
              sx={{
                fontSize: { xs: '16px', sm: '20px' },
                lineHeight: { xs: '19px', sm: '23px' },
                fontWeight: '500',
              }}
            >
              {statusText}
            </Typography>
          ) : (
            <Box
              sx={{ maxWidth: { xs: '98px', sm: 'auto' } }}
              component="img"
              src={LOGO_LIGHT}
            />
          )}
        </Box>
        <Box
          sx={{
            alignSelf: 'flex-end',
            display: 'grid',
            columnGap: { xs: 1.3, sm: 1.3 },
            rowGap: { xs: 1.9, sm: 2.5 },
            gridTemplateColumns: {
              xs: '1fr 10% 23%',
              sm: '1fr 8% 18%',
              md: '1fr 8% 26%',
              lg: '1fr 5% 25%',
            },
            marginBottom: '20px',
          }}
        >
          {items.map((orderItem) => (
            <OrderItem
              key={orderItem.id}
              title={orderItem.item_name}
              count={orderItem.count}
              priceKop={orderItem.price_kop}
            />
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          paddingTop: { xs: '13px', sm: '30px' },
          alignSelf: 'flex-end',
          display: 'grid',
          columnGap: { xs: 1.3, sm: 1.3 },
          rowGap: { xs: 1.9, sm: 2.5 },
          gridTemplateColumns: {
            xs: '1fr 0.4fr',
            sm: '1fr 0.4fr',
          },
          justifyItems: 'flex-end',
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '20px' },
            lineHeight: { xs: '19px', sm: '23px' },
            fontWeight: '500',
          }}
        >
          {t('promo_benefits')}:
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '20px' },
            lineHeight: { xs: '19px', sm: '23px' },
            fontWeight: '300',
          }}
        >
          {benefitСonvertedPrice}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '20px' },
            lineHeight: { xs: '19px', sm: '23px' },
            fontWeight: '500',
          }}
        >
          {t('delivery_cost')}:
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '20px' },
            lineHeight: { xs: '19px', sm: '23px' },
            fontWeight: '300',
          }}
        >
          {deliveryConvertedPrice}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '20px' },
            lineHeight: { xs: '19px', sm: '23px' },
            fontWeight: '500',
          }}
        >
          {t('total')}:
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '16px', sm: '20px' },
            lineHeight: { xs: '19px', sm: '23px' },
            fontWeight: '300',
          }}
        >
          {resultTotalConvertedPrice}
        </Typography>
      </Box>
    </Box>
  );
};

export default OrderList;
